import { useCallback, useEffect, useState } from 'react';
import { t } from '@lingui/macro';

import _ from 'lodash';
import { makeStyles } from '@material-ui/core/styles';
import Panel from '../../page-framework/panel/PanelDecorator';
import { panelPropDefaults, panelPropTypes } from '../../page-framework/panel/PanelPropTypes';
import { COLUMN_TYPES } from '../../data-table/DataTableColumns';
import { useQueryNowLoader } from '../../data-table/Loaders';
import { filterId as testFilterId } from '../../filters/test-filter/TestFilter';
import { businessServiceFilterId, siteFilterId, timeRangeFilterId } from '../../filters';
import { Ng1DrillDownButton } from '../../ng1-drill-down/Ng1DrillDownButton';
import useNg1DrillDownService from '../../../services/Ng1DrillDownService';
import { FORMATTER_TYPES } from '../../../utilities/FormattingUtilities';
import { SELECTIONMODE_MULTI } from '../../data-table/SelectionContext';
import ReportingDataTable from '../../data-table/ReportingDataTable';
import { formatMos } from '../../../utilities/MetricFormatter';
import { useDataService } from '../../../services/ApiDataService';
import { VOIP_PHONE_TEST_TYPE, VOIP_PULSE_TEST_TYPE } from './VoipTestConstants';
import { QUICK_TEST_STATUS } from '../quick-test-panel/QuickTestConstants';

const title = t`Service Tests`;

const TEST_TYPE_URL = '/ipm/v1/open/testTypes';

const useStyles = makeStyles((theme) => ({
    mosNormal: {
        borderBottom: `3px solid ${theme.palette.status.green}`,
    },

    mosDegraded: {
        borderBottom: `3px solid ${theme.palette.status.orange}`,
    },

    mosCritical: {
        borderBottom: `3px solid ${theme.palette.status.red}`,
    },
}));

let dataSource = {
    url: '/ipm/v1/admin/quicktests',
    totalProperty: 'count',
    dataProperty: 'data',
    isResultsPage: false,
    numericMetrics: ['numAgents'],
    metricsToNotSort: ['createdAt'],
    params: {
        searchFields: [
            'status',
            'name',
            'testConfig.caller.name',
            'testConfig.callee.name',
            'testConfig.phone',
            'createdBy',
        ],
        query: JSON.stringify({ quickTestType: 'voip' }),
    },
};

const getThresholdStatus = (mos, threshold) => {
    if (!mos) {
        return 'empty';
    }

    if (mos >= threshold.degraded) {
        return 'normal';
    }

    if (mos < threshold.degraded && mos >= threshold.critical) {
        return 'degraded';
    }

    return 'critical';
};

const formatThresholdStatus = (value, voipTestType) => {
    const threshold = _.first(voipTestType.thresholds);

    value.callerThresholdStatus = getThresholdStatus(value.summaryCallerMOS, threshold);
    value.calleeThresholdStatus = getThresholdStatus(value.summaryCalleeMOS, threshold);

    return value;
};

const buildMOSTemplate = (classes, row, mapping, voipTestType, statusKey) => {
    const value = row[mapping];
    if (_.isNil(value)) return;
    const valueFmt = formatMos(value);
    const status = formatThresholdStatus(row, voipTestType)[statusKey];
    const thresholdStatus = `mos${status.charAt(0).toUpperCase()}${status.slice(1)}`;
    return <span className={classes[thresholdStatus]}>{valueFmt}</span>;
};

const getVoIPColumns = (voipTestType, classes) => {
    return [
        {
            type: COLUMN_TYPES.LINK,
            title: t`Run Date/Time`,
            mapping: 'createdAt',
            sortable: true,
            width: 250,
            formatter: FORMATTER_TYPES.DATETIME,
            link: {
                target: `quickTests/voip/:_id`,
                mappedFilterParams: {
                    [testFilterId]: 'test',
                },
            },
            csvFormatter: (value, row) => row.createdAt,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Caller`,
            sortable: true,
            mapping: 'testConfig.caller.name',
            width: 220,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Callee`,
            sortable: true,
            mapping: 'testConfig.callee.name',
            width: 220,
        },
        {
            type: COLUMN_TYPES.NUMERIC,
            title: 'Caller MOS',
            sortable: true,
            mapping: 'summaryCallerMOS',
            width: 100,
            formatter: (value) => formatMos(value),
            renderer: (row) =>
                buildMOSTemplate(classes, row, 'summaryCallerMOS', voipTestType, 'callerThresholdStatus'),
        },
        {
            type: COLUMN_TYPES.NUMERIC,
            title: 'Callee MOS',
            sortable: true,
            mapping: 'summaryCalleeMOS',
            width: 100,
            formatter: (value) => formatMos(value),
            renderer: (row) =>
                buildMOSTemplate(classes, row, 'summaryCalleeMOS', voipTestType, 'calleeThresholdStatus'),
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Interface`,
            width: 100,
            mapping: 'adapterType',
            csvFormatter: (value, row) => row.adapterType,
            sortable: true,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Status`,
            mapping: 'status',
            resizable: false,
            width: 150,
            csvFormatter: (value, row) => row.status,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Created By`,
            mapping: 'createdBy',
            sortable: true,
            csvFormatter: (value, row) => row.createdBy,
        },
    ];
};

const sorters = [{ property: 'createdAt', direction: 'desc' }];

const NG1_DRILLDOWN_COLUMN = {
    type: COLUMN_TYPES.ICON,
    mapping: 'test',
    width: 30,
    uiOnly: true,
    icons: [
        {
            getIcon: (row) => {
                return <Ng1DrillDownButton test={row?.test} />;
            },
            getTooltip: () => t`Launch nGeniusONE Smart Edge Service Monitor`,
        },
    ],
};

function VoipTestNowTestListPanel({
    tableState,
    setTableState,
    tableSelection,
    setTableSelection,
    setDeleteDisabled,
    setReRunDisabled,
    triggerRef,
    ...props
}) {
    const classes = useStyles();
    const dataService = useDataService();
    const loader = useQueryNowLoader(dataSource);
    const { hasNg1DrillDownPrivilege } = useNg1DrillDownService();
    const [voipTestType, setVoipTestType] = useState();
    const [columns, setColumns] = useState([]);

    const handleTableSelectionChange = (currentTableSelection) => {
        setTableSelection(currentTableSelection);
        setDeleteDisabled(
            !currentTableSelection || (!currentTableSelection.selection.length && !currentTableSelection.inverted)
        );

        // currentTableSelection.inverted is true when the very top box is checked that selects everything.
        const oneTestSelected = currentTableSelection && currentTableSelection.selection.length === 1;
        setReRunDisabled(!oneTestSelected || currentTableSelection.inverted);
    };

    const getPulseTestType = useCallback(async () => {
        const params = {
            query: JSON.stringify({ name: VOIP_PULSE_TEST_TYPE }),
        };
        const response = await dataService.get(TEST_TYPE_URL, params);
        return response[0];
    }, [dataService]);

    const getPhoneTestType = useCallback(async () => {
        const params = {
            query: JSON.stringify({ name: VOIP_PHONE_TEST_TYPE }),
        };
        const response = await dataService.get(TEST_TYPE_URL, params);
        return response[0];
    }, [dataService]);

    useEffect(() => {
        async function fetchData() {
            const pulseTestType = await getPulseTestType();
            const phoneTestType = await getPhoneTestType();
            setVoipTestType(pulseTestType);

            const dataSourceQueryParams = {
                searchFields: [
                    'status',
                    'name',
                    'testConfig.caller.name',
                    'testConfig.callee.name',
                    'testConfig.phone',
                    'createdBy',
                ],
                query: JSON.stringify({
                    type: [pulseTestType._id, phoneTestType._id],
                    verify: false,
                    status: { $in: _.values(QUICK_TEST_STATUS) },
                }),
            };
            dataSource = {
                ...dataSource,
                params: dataSourceQueryParams,
            };
        }

        fetchData();
    }, [getPhoneTestType, getPulseTestType]);

    useEffect(() => {
        if (voipTestType) {
            if (hasNg1DrillDownPrivilege()) {
                setColumns([...getVoIPColumns(voipTestType, classes), NG1_DRILLDOWN_COLUMN]);
            } else {
                setColumns(getVoIPColumns(voipTestType, classes));
            }
        }
    }, [voipTestType, hasNg1DrillDownPrivilege, classes]);

    return (
        <ReportingDataTable
            panelProps={props}
            loader={loader}
            columns={columns}
            sorters={sorters}
            visibleRows={0}
            deletedMapping="test.deleted"
            showSelectionColumn
            onStateChanged={setTableState}
            triggerRef={triggerRef}
            selectionOptions={{
                mode: SELECTIONMODE_MULTI,
                allowSelectAll: true,
                idMapping: '_id',
                onChange: handleTableSelectionChange,
            }}
            disableColumnRemover
        />
    );
}

VoipTestNowTestListPanel.propTypes = panelPropTypes;

VoipTestNowTestListPanel.defaultProps = {
    ...panelPropDefaults,
    title,
    autoHeight: true,
    minContentHeight: 130,
};

export default Panel({
    typeAlias: 'VoipTestList',
    filters: {
        required: [timeRangeFilterId],
        optional: [businessServiceFilterId, siteFilterId],
    },
})(VoipTestNowTestListPanel);
